import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import userService, { type SignInReq } from "@/api/services/userService";

import { toast } from "sonner";
import type { Permission, Role, UserInfo, UserToken } from "#/entity";
import { StorageEnum } from "#/enum";
import { ADMIN_ROLE } from "@/_mock/assets";
import { PERMISSION_LIST } from "@/_mock/assets";

const { VITE_APP_HOMEPAGE: HOMEPAGE } = import.meta.env;

type UserStore = {
	userInfo: Partial<UserInfo>;
	userToken: UserToken;
	eventToken: string;
	actions: {
		setUserInfo: (userInfo: UserInfo) => void;
		setUserToken: (token: UserToken) => void;
		setEventToken: (eventToken: string) => void;
		clearUserInfoAndToken: () => void;
	};
};

const useUserStore = create<UserStore>()(
	persist(
		(set) => ({
			userInfo: {},
			userToken: {},
			eventToken: "",
			actions: {
				setUserInfo: (userInfo) => {
					set({ userInfo });
				},
				setUserToken: (userToken) => {
					set({ userToken });
				},
				setEventToken: (eventToken) => {
					set({ eventToken });
				},
				clearUserInfoAndToken() {
					set({ userInfo: {}, userToken: {}, eventToken: "" });
				},
			},
		}),
		{
			name: "userStore", // name of the item in the storage (must be unique)
			storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used
			partialize: (state) => ({
				[StorageEnum.UserInfo]: state.userInfo,
				[StorageEnum.UserToken]: state.userToken,
				[StorageEnum.EventToken]: state.eventToken,
			}),
		},
	),
);

export const useUserInfo = () => useUserStore((state) => state.userInfo);
export const useUserToken = () => useUserStore((state) => state.userToken);
export const useEventToken = () => useUserStore((state) => state.eventToken);
export const useUserPermission = () => useUserStore((state) => state.userInfo.permissions);
export const useUserActions = () => useUserStore((state) => state.actions);

export const useSignIn = () => {
	const navigatge = useNavigate();
	const { setUserToken, setUserInfo } = useUserActions();
	const userToken = useUserToken();
	const signInMutation = useMutation({
		mutationFn: userService.signin,
	});

	const signIn = async (data: SignInReq) => {
		try {
			const res = await signInMutation.mutateAsync(data);
			const { token } = res;
			setUserToken({ token });
			setUserInfo({
				id: "1",
				username: data.loginName,
				role: ADMIN_ROLE as Role,
				permissions: ADMIN_ROLE.permission as Permission[],
			});
			navigatge(HOMEPAGE, { replace: true });
			toast.success("Sign in success!", {
				closeButton: true,
			});
		} catch (err) {
			toast.error(err.message, {
				position: "top-center",
			});
			console.log("userToken", userToken);
		}
	};

	return signIn;
};

export default useUserStore;
